/**
 * SiteWorkCard — per-structure earthwork + parcel-wide driveway cost
 * ranges + site condition facts, in the units a real contractor will
 * quote in. The view exposes everything we have, no totals and no
 * recommendations — see the explicit non-goals in the plan.
 *
 * Empty states are first-class: the body stays mounted even when no
 * structures are placed or env data hasn't landed, so its position in
 * the report rail is stable across the loading sequence.
 */
import { useState, type ReactElement } from "react";
import { IconChevronDown } from "@tabler/icons-react";
import type {
  SiteWorkEarthworkRow,
  SiteWorkEstimate,
  SiteWorkLineItem
} from "../../score/computeSiteWorkEstimate";
import type { InspectorPresentation } from "./BaseLayersCard";
import { InspectorChipButton, InspectorChipButtonRow } from "./InspectorChipButton";
import { LineItem } from "./LineItem";
import { Spacer } from "../Spacer";
import { SidetextRow } from "./SidetextRow";
import { SubsectionHeader } from "./SubsectionHeader";
import styles from "./SiteWorkCard.module.css";

export interface SiteWorkCardProps {
  estimate: SiteWorkEstimate;
  /** Triggered by the "Print contractor summary" button. Hidden when null. */
  onPrintHandoff?: () => void;
  presentation?: InspectorPresentation;
}

export function SiteWorkCard({
  estimate,
  onPrintHandoff,
  presentation = "inspector"
}: SiteWorkCardProps): ReactElement {
  const drawerRail = presentation === "drawer";
  const factRows = buildSiteFactRows(estimate.siteFacts);
  const hasEarthwork = estimate.earthworkPerStructure.length > 0;
  const hasDriveway = estimate.drivewayInstall != null;

  return (
    <div>
      <SubsectionHeader label="Earthwork" first />
      {!hasEarthwork ? (
        <p className={styles.emptyState}>
          Place a structure to see earthwork volume and its cost range.
        </p>
      ) : (
        estimate.earthworkPerStructure.map((row) => (
          <EarthworkLine key={row.structureId} row={row} drawerRail={drawerRail} />
        ))
      )}

      <SubsectionHeader label="Driveway install" />
      {hasDriveway ? (
        <LineItem
          title="Driveway"
          value={formatCostRange(estimate.drivewayInstall as SiteWorkLineItem)}
          note={
            <>
              {(estimate.drivewayInstall as SiteWorkLineItem).units}
              {" · "}
              {(estimate.drivewayInstall as SiteWorkLineItem).source}
            </>
          }
          drawerRail={drawerRail}
        />
      ) : (
        <p className={styles.emptyState}>
          Driveway estimate appears once road context loads and the
          buildability analysis finishes.
        </p>
      )}

      <SubsectionHeader label="Site facts" />
      {factRows.map((r) => (
        <SidetextRow
          key={r.label}
          label={r.label}
          sideText={r.sideText}
          drawerRail={drawerRail}
        />
      ))}

      <AboutDisclosure estimate={estimate} />

      {onPrintHandoff ? (
        <>
          <Spacer size="xs" noBorder />
          <InspectorChipButtonRow>
            <InspectorChipButton fullWidth drawerRail={drawerRail} onClick={onPrintHandoff}>
              Print contractor summary (PDF)
            </InspectorChipButton>
          </InspectorChipButtonRow>
        </>
      ) : null}
    </div>
  );
}

function EarthworkLine({
  row,
  drawerRail
}: {
  row: SiteWorkEarthworkRow;
  drawerRail: boolean;
}): ReactElement {
  return (
    <LineItem
      title={row.structureName}
      value={formatCostRange(row.item)}
      metrics={[
        { label: "Cut", value: formatYd3(row.cutYd3) },
        { label: "Fill", value: formatYd3(row.fillYd3) },
        { label: "Net", value: formatSignedYd3(row.netYd3) }
      ]}
      note={
        <>
          {row.item.units}
          {" · "}
          {row.item.source}
        </>
      }
      drawerRail={drawerRail}
    />
  );
}

/** Regional multiplier + scope disclaimer, folded behind one disclosure row. */
function AboutDisclosure({ estimate }: { estimate: SiteWorkEstimate }): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.about}>
      <button
        type="button"
        className={styles.aboutToggle}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>About this estimate</span>
        <IconChevronDown
          size={16}
          stroke={1.5}
          className={`${styles.aboutChevron}${open ? ` ${styles.aboutChevronOpen}` : ""}`}
        />
      </button>
      {open ? (
        <div className={styles.aboutBody}>
          <p>
            Regional cost multiplier{" "}
            <strong>{estimate.regionalMultiplier.value.toFixed(2)}×</strong> —{" "}
            {estimate.regionalMultiplier.basis}.
          </p>
          <p>
            <strong>What this is.</strong> A pre-visit feasibility estimate for
            the site-work portion only. <strong>What it isn't.</strong>{" "}
            Building construction, permits, utility hookups, well/septic, tree
            clearing, and engineered foundation design are excluded — get those
            from a contractor using the summary below.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function formatCostRange(item: SiteWorkLineItem): string {
  if (item.costLow === 0 && item.costHigh === 0) return "—";
  return `${formatUsd(item.costLow)} – ${formatUsd(item.costHigh)}`;
}

function formatUsd(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "$0";
  return `$${Math.round(value).toLocaleString("en-US")}`;
}

function formatYd3(value: number): string {
  const rounded = Math.round(value);
  return rounded === 0 ? "0" : `${rounded.toLocaleString("en-US")} yd³`;
}

function formatSignedYd3(value: number): string {
  const rounded = Math.round(value);
  if (rounded === 0) return "0";
  const abs = Math.abs(rounded).toLocaleString("en-US");
  return rounded > 0 ? `+${abs} yd³` : `\u2212${abs} yd³`;
}

interface FactRow {
  label: string;
  sideText: string;
}

function buildSiteFactRows(facts: SiteWorkEstimate["siteFacts"]): FactRow[] {
  return [
    {
      label: "Soil drainage",
      sideText: facts.soilDrainageClass ?? "\u2014"
    },
    {
      label: "Hydric soil",
      sideText: facts.soilHydric ?? "\u2014"
    },
    {
      label: "Pad slope (best buildable)",
      sideText:
        facts.bestBuildableAvgSlopePercent != null
          ? `${facts.bestBuildableAvgSlopePercent.toFixed(1)}%`
          : "\u2014"
    },
    {
      label: "FEMA flood zone",
      sideText: facts.floodZoneCode ?? "\u2014"
    },
    {
      label: "Wetlands coverage",
      sideText:
        facts.wetlandsCoverageFraction != null
          ? formatCoverage(facts.wetlandsCoverageFraction)
          : "\u2014"
    },
    {
      label: "Drainage sinks in parcel",
      sideText: facts.drainageSinksInsideParcel.toLocaleString("en-US")
    }
  ];
}

function formatCoverage(fraction: number): string {
  if (fraction <= 0) return "0% (none)";
  if (fraction < 0.005) return "<1%";
  return `${Math.round(fraction * 100)}%`;
}
